import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react'
import { FlatList, ListRenderItemInfo } from 'react-native'
import { useForm } from 'react-hook-form'
import pick from 'lodash/pick'
import styled from 'styled-components/native'
import { Portal } from 'APP/Helpers/Portal'
import { isMobile } from 'APP/Helpers/checkPlatform'
import I18n from 'APP/I18n'
import { usePlacesSearch } from 'APP/Hooks/PlacesSearch'
import { transformAnswer } from 'APP/Lib/PlacesHelper'
import { PlaceResult, searchAddress as searchAddressHelper } from 'APP/Lib/AddressSearchHelpers'
import { APP_SENDER } from 'APP/Lib/constants'
import ProgressBar from 'APP/Converse/ProgressBar'
import Button from 'APP/Converse/Button'
import { PromptChoiceSingleController } from './PromptChoiceSingleController'
import { PromptAddressController } from './PromptAddressController'
import { ConditionalOverlayForm } from './ConditionalOverlayForm'
import IntakeQuestion from '../IntakeQuestion'
import { PromptTextControllers } from './PromptTextControllers'
import {
  MultiSubPromptItem,
  MultiSubPromptType,
  PromptAddressItemProps,
  PromptChoiceSingleItemProps,
  PromptTextItemProps,
} from './types'
import type { MemberAppFullscreen } from 'APP/Services/Mattermost/types'
import { logger } from 'APP/Lib/Datadog'

interface PrefilledPrompt {
  type: string
  choices?: Array<{ id: string; display: string; payload?: unknown; description?: string }>
  prefilled_values?: null | string | boolean | number | (string | boolean | number)[]
}

interface ResponseValue {
  display?: string
  props?: Record<string, unknown>
}

type FormValue = ResponseValue | string | number | boolean | (string | number | boolean)[] | null
type MultiSubPromptForm = Record<string, FormValue>

interface ConditionalFormKey {
  fieldName: string
  item: PromptAddressItemProps
}

interface IntakeMultiSubPromptProps {
  prompts: MultiSubPromptItem[]
  sendAnswer: (answers: Record<string, unknown>[], display: string) => void
  deactivatePortal: boolean
  disabled?: boolean
  id?: string
  title?: string
  utterance?: string | null
  member_app_fullscreen?: Partial<MemberAppFullscreen>
}

const isTextPrompt = (prompt: MultiSubPromptItem): prompt is PromptTextItemProps =>
  prompt.type === MultiSubPromptType.TEXT && 'text_type' in prompt

const isAddressPrompt = (prompt: MultiSubPromptItem): prompt is PromptAddressItemProps =>
  prompt.type === MultiSubPromptType.ADDRESS && 'place_type' in prompt

const isChoicePrompt = (prompt: MultiSubPromptItem): prompt is PromptChoiceSingleItemProps =>
  prompt.type === MultiSubPromptType.CHOICE_SINGLE && 'choices' in prompt

const ButtonContainer = styled.View`
  padding-left: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-right: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-top: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  padding-bottom: ${({ theme }) => theme.insets.bottom + theme.metrics.baseMargin}px;
`

const Container = styled.View<{ $useSingleScrollLayout: boolean }>`
  flex-grow: 1;
  ${({ $useSingleScrollLayout }) => $useSingleScrollLayout && 'flex: 1;'}
`

const Footer = styled.View`
  flex-grow: 1;
  justify-content: flex-end;
`

const extendData = (promptType: string, values: FormValue) => {
  switch (promptType) {
    case MultiSubPromptType.CHOICE_SINGLE:
    case MultiSubPromptType.ADDRESS:
      if (typeof values === 'object' && values && !Array.isArray(values) && values.props)
        return values.props
      break
    case MultiSubPromptType.TEXT:
      if (values) return { text: values }
      break
  }
  return values
}

const subResponseString = (promptType: string, response: FormValue) => {
  switch (promptType) {
    case MultiSubPromptType.CHOICE_SINGLE:
    case MultiSubPromptType.ADDRESS:
      return typeof response === 'object' && response && !Array.isArray(response)
        ? response.display ?? ''
        : ''
    case MultiSubPromptType.TEXT:
      return response ?? ''
    default:
      return ''
  }
}

// Function to build the prefilled value based on the prompt type
export const prefilledValueBuilder = async (
  prompt: PrefilledPrompt,
  searchAddress: (fullAddress: string) => Promise<PlaceResult | null>
): Promise<FormValue> => {
  switch (prompt.type) {
    case MultiSubPromptType.CHOICE_SINGLE: {
      const value = prompt.choices?.find((el) => el.id === prompt.prefilled_values)
      return value
        ? {
            display: value.display,
            props: { selected_choice: value },
          }
        : null
    }
    case MultiSubPromptType.ADDRESS: {
      if (typeof prompt.prefilled_values !== 'string' || !prompt.prefilled_values) {
        return null
      }

      const answer = await searchAddress(prompt.prefilled_values)
      const selected_address = answer && transformAnswer(answer)
      return selected_address
        ? {
            display: selected_address.address,
            props: {
              selected_address,
            },
          }
        : null
    }
    case MultiSubPromptType.TEXT:
      return prompt.prefilled_values ?? null

    default:
      return null
  }
}

const fieldNameBuilder = (index: number, type: string) => `${index}_${type}`

const IntakeMultiSubPrompt = ({
  prompts: promptsProp,
  sendAnswer,
  deactivatePortal,
  disabled,
  ...props
}: IntakeMultiSubPromptProps) => {
  const [, , resolvePlace, getPlacePredictions] = usePlacesSearch()
  // Prefilled values are initialized once for the prompt set mounted with this form.
  const prefillConfig = useRef({ getPlacePredictions, promptsProp, resolvePlace })
  const [conditionalFormKey, setConditionalFormKey] = useState<ConditionalFormKey | null>(null)
  const [focusedSubPrompt, setFocusedSubPrompt] = useState<string | null>(null)

  const displayProgressBar = props?.member_app_fullscreen?.show_question_progress
  const {
    watch,
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState: { isValid },
  } = useForm<MultiSubPromptForm>({ mode: 'all' })

  // useEffect for fetching and setting prefilled values
  useEffect(() => {
    const fetchPrefilledValues = async () => {
      try {
        await Promise.allSettled(
          prefillConfig.current.promptsProp.map(async (prompt, index) => {
            const name = fieldNameBuilder(index, prompt.type)
            const prefilledValue = await prefilledValueBuilder(prompt, (fullAddress) =>
              searchAddressHelper(
                fullAddress,
                prefillConfig.current.getPlacePredictions,
                prefillConfig.current.resolvePlace
              )
            )

            if (prefilledValue) {
              setValue(name, prefilledValue, { shouldValidate: true })
            }
          })
        )
      } catch (error) {
        logger.error('Error fetching prefilled values:', {}, error)
      }
    }
    fetchPrefilledValues()
  }, [setValue])

  const prompts = useMemo(() => {
    const showAsteriskForRequiredFields = !promptsProp.every((p) => p.required)
    return promptsProp.map((prompt) => {
      return {
        ...prompt,
        showRequiredAsterisk: Boolean(showAsteriskForRequiredFields && prompt.required),
      }
    })
  }, [promptsProp])

  const sendAdditionalAnswer = useCallback(
    (conditionalFormValue: unknown) => {
      if (!conditionalFormKey) return
      setValue(conditionalFormKey.fieldName, conditionalFormValue as FormValue, {
        shouldValidate: true,
      })

      setConditionalFormKey(null)
    },
    [setValue, conditionalFormKey]
  )

  const renderSubPromptListItem = ({
    item,
    index,
  }: ListRenderItemInfo<MultiSubPromptItem>): React.ReactElement | null => {
    const name = fieldNameBuilder(index, item.type)

    if (isTextPrompt(item)) {
      return (
        <PromptTextControllers
          name={name}
          item={item}
          control={control}
          trigger={trigger}
          setValue={setValue}
          clearErrors={clearErrors}
          getValues={getValues}
          isFocused={focusedSubPrompt === name}
          onFocus={setFocusedSubPrompt}
        />
      )
    }

    if (isAddressPrompt(item)) {
      return (
        <PromptAddressController
          name={name}
          item={item}
          control={control}
          openConditionalForm={setConditionalFormKey}
          isFocused={focusedSubPrompt === name}
          onFocus={setFocusedSubPrompt}
        />
      )
    }

    if (isChoicePrompt(item)) {
      return (
        <PromptChoiceSingleController
          name={name}
          control={control}
          item={item}
          memberAppFullscreen={props?.member_app_fullscreen}
          utterance={props?.utterance}
          onFocus={setFocusedSubPrompt}
        />
      )
    }

    return null
  }

  const submitForm = useCallback(
    (values: MultiSubPromptForm) => {
      const answers = prompts.reduce<Record<string, unknown>[]>((acc, prompt, index) => {
        const promptAnswers = pick(prompt, ['text_type', 'place_type', 'payload'])
        const extendedData = extendData(prompt.type, values[`${index}_${prompt.type}`])

        // omit empty optional responses
        if (
          !extendedData ||
          typeof extendedData !== 'object' ||
          Object.values(extendedData).every((value) => !value)
        )
          return acc

        return [
          ...acc,
          {
            sender: APP_SENDER,
            type: `${prompt.type}_response`,
            ...promptAnswers,
            ...extendedData,
          },
        ]
      }, [])

      const display = prompts
        .map((prompt, index) =>
          I18n.t('ConversationScreen.multiSubPrompt.response', {
            index: index + 1,
            question: prompt.title,
            response: subResponseString(prompt.type, values[`${index}_${prompt.type}`]),
            interpolation: { escapeValue: false },
          })
        )
        .join('\n---\n\n')

      sendAnswer(answers, display)
    },
    [prompts, sendAnswer]
  )

  const onCloseConditionModal = useCallback(() => {
    setConditionalFormKey(null)
  }, [])

  const allFields = watch()
  const progress = useMemo(() => {
    const totalFields = prompts.length
    if (!totalFields) return 0

    const filledFields = prompts.filter((prompt, index) =>
      Boolean(allFields[fieldNameBuilder(index, prompt.type)])
    ).length

    return (filledFields / totalFields) * 100
  }, [allFields, prompts])

  // Native mobile uses a single scroll owner so nested scroll views cannot interfere with
  // scrolling or clip the final prompt behind the fixed portal footer. Web keeps its layout.
  const useSingleScrollLayout = isMobile()

  return (
    <Container $useSingleScrollLayout={useSingleScrollLayout}>
      <FlatList
        testID="INTAKE_MULTI_SUBPROMPT_LIST"
        data={prompts}
        {...(useSingleScrollLayout ? { removeClippedSubviews: false } : {})}
        keyExtractor={(prompt, index) => `${prompt.title ?? prompt.type} ${index}`}
        renderItem={renderSubPromptListItem}
        stickyHeaderIndices={useSingleScrollLayout ? undefined : [0]}
        ListHeaderComponent={
          <IntakeQuestion
            post={{ id: props.id ?? '', message: props.title ?? '', props }}
            animated={false}
            scrollEnabled={false}
          />
        }
        ListFooterComponent={
          <Footer>
            <Portal name="multiSubPromptSubmitPortal" deactivate={deactivatePortal}>
              {displayProgressBar && <ProgressBar progress={progress} />}
              <ButtonContainer>
                <Button
                  variant="primary"
                  widthVariant="full"
                  testID="INTAKE_MULTI_SUBPROMPT_NEXT_BTN"
                  analyticsName="IntakeComponent.multiSubPrompt.next"
                  title={I18n.t('IntakeComponent.next')!}
                  disabled={disabled || !isValid}
                  onPress={() => handleSubmit(submitForm)()}
                />
              </ButtonContainer>
            </Portal>
          </Footer>
        }
        contentContainerStyle={useSingleScrollLayout ? undefined : { flexGrow: 1 }}
      />
      {!!conditionalFormKey && (
        <ConditionalOverlayForm
          sendAnswer={sendAdditionalAnswer}
          onClose={onCloseConditionModal}
          conditionalOverlayFormData={conditionalFormKey.item}
        />
      )}
    </Container>
  )
}

export default IntakeMultiSubPrompt
